import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Square } from 'lucide-react';
import { Spinner } from '../../Spinner';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useTranslation } from '../../../lib/i18n';
import { useMyAgents } from '../../../lib/my-agents';
import { useDuplexer } from '../../../lib/use-duplexer';
import { taskStatusBadge } from '../../../lib/task-status';
import { AgentDot } from '../../AgentDot';
import { LinkifiedText } from '../../LinkifiedText';
import { Badge, Button, Card, ScrollArea, Typography } from '../ui';
import { useTypewriter } from '../chat-surface/loaders';
import {
  buildRows,
  collapseRepeatedActs,
  entityToMessage,
  isTerminal,
  type TranscriptMessage,
} from '../tasks/agent-conversation-rows';
import { agentAtWorkWindow, showsCardAttribution, upsertAgentAtWorkRow, VERDICT_BADGE } from '../../../lib/agent-at-work';
import styles from './AgentAtWorkCard.module.css';

/**
 * One transcript line, revealed with the SAME typewriter the chat's own bubbles use, so a turn arriving
 * here reads like a turn arriving there rather than snapping in whole.
 *
 * `animate` is captured ONCE and never re-read. Flipping `useTypewriter`'s `skipInitial` mid-reveal
 * re-runs its effect, which early-returns and clears the interval — freezing the text truncated for good
 * (the trap written up against `MessageRow` in this package's CLAUDE.md). So a row that stops being the
 * newest keeps typing to completion instead of locking mid-word.
 */
function CardTurnText({ text, animate }: { text: string; animate: boolean }) {
  const [types] = useState(animate);
  return <LinkifiedText text={useTypewriter(text, !types)} />;
}

/** A settled task has nothing left to reach FOR: a plan step the backend never flagged done (the common
 * case for a task that finished — or failed — without every trailing step marked complete) must not go
 * on reading as "not started yet" once there is no more run to start it. Mirrors iOS/Android 1:1. */
function planStepState(index: number, currentIndex: number | null, terminal: boolean): ChecklistState {
  if (currentIndex === null || terminal) return 'done';
  if (index < currentIndex) return 'done';
  if (index === currentIndex) return 'current';
  return 'upcoming';
}

/** One checklist line: a check (done), a spinner + emphasis (current), or a plain dot (upcoming) leading
 * the sentence. Shared by the real plan checklist AND — when the worker card has no real plan yet — its
 * own transcript rows, so there is never a stray line with no prefix or a size that doesn't match its
 * neighbours. */
function ChecklistRow({ text, state }: { text: string; state: ChecklistState }) {
  return (
    <div className={styles.step}>
      {state === 'current' ? (
        <Spinner size={14} />
      ) : state === 'done' ? (
        <Check size={14} aria-hidden className={styles.stepCheck} />
      ) : (
        <span className={styles.stepDot} aria-hidden />
      )}
      <Typography variant="p2" color={state === 'current' ? 'primary' : 'secondary'} className={state === 'current' ? styles.stepCurrent : undefined}>
        {text}
      </Typography>
    </div>
  );
}

// The in-chat "agent at work" moment — the live counterpart to `AgentJoinCard`. Rendered in place of a
// text bubble for an ASSISTANT message carrying `metadata.agentAtWorkCard`, which the Maestro emits when
// it spawns an agent onto a task.
//
// Where the join card is a SNAPSHOT of something that happened, this is a WINDOW on work still in motion.
// **The chat row it rides carries only `taskId`** — everything on screen is read live off that task (a
// history row cannot be updated as the work proceeds without invalidating every cached token behind it),
// via `/api/task/:id/at-work`: the working agent's name and avatar, the task's current status, the
// sentence it wrote about what it is doing, and the newest turns of its own conversation streaming in.
//
// A task has TWO sides — the agent doing the work and the agent reviewing it — and each gets its OWN
// card: the worker's card always renders, and a second reviewer card appears directly below it the
// moment a review pass exists (`hasReviewer`). Both are read live off the same task, so the reviewer
// card can appear mid-run.
//
// The live pieces come from two sources, split by what can push: the ROWS arrive over the chat duplexer
// channel, while the title, status and action sentence have no push and ride a slow poll.
// That is the same division the task screen's `AgentConversationPanel` makes, and the fold and the row
// shapes are literally the same code (`agent-conversation-rows.ts`), so a beat cannot read one way in the
// card and another on the task screen.
//
// Stop halts the run AND tells the MAESTRO its task was called off — one server action
// (`/api/tasks/:id/cancel` → native-data `task-cancel`), the same one the task screen's Cancel uses. It
// requests the per-conversation stop flag, and writes a SYSTEM row into the Maestro's chat.
//
// What Stop guarantees is "nothing further HAPPENS", not that the run dies on the click. The sub-agent's
// completion arrives as a separate event (there is no local stream to abort, and nothing in this codebase
// cancels a prediction at the gateway), so the model call in flight finishes — but the loop then reads the
// task's own status before running a single tool and ends there. So no tool call, no file, no message after
// the press; only a tool already mid-execution finishes, because tools take no AbortSignal.
//
// The task's STATUS is the signal, deliberately, not the per-conversation Stop flag: `BaseKVStore` is
// scoped per ARTIFACT, so a flag the serverless server sets is invisible to the harness loop. That was a
// real bug — a stopped run wrote a file two minutes later. Stop shows only while the task is unfinished.
//
// Shared 1:1 with the native `AgentAtWorkCard`s.

// The duplexer streams new rows; this slow poll refreshes the title, status and action sentence (nothing
// pushes those), notices a reviewer that starts mid-run, and reconciles a dropped socket.
// 15s, matching `AgentConversationPanel`'s own fallback so the two never disagree about freshness.
const POLL_MS = 15000;

export interface AgentAtWorkCardMarker {
  taskId: string;
  agentId?: string;
  agentName?: string;
  agentTitle?: string;
  taskTitle?: string;
}

interface AtWorkResponse {
  found: boolean;
  workerConversationId?: string;
  reviewerConversationId?: string;
  reviewerAgentId?: string;
  /** Where the review stands — the reviewer's verdict and its own words. null before the first submission. */
  reviewVerdict?: { stage: 'submitted' | 'approved' | 'changesRequested' | 'failed'; content: string } | null;
  workerRows?: TranscriptMessage[];
  reviewerRows?: TranscriptMessage[];
  title?: string;
  status?: string;
  currentAction?: string;
  isLive?: boolean;
  /** The task's real sub-task list — the checklist's source of truth. */
  plan?: { title: string; completed: boolean }[];
}

/** One checklist line's visual state — shared by the real plan checklist AND, on the worker card with
 * no real plan yet, the transcript rows themselves (see `renderTranscript`): a DONE step wears a check,
 * the CURRENT one a spinner with emphasis, and one that hasn't started yet a plain dot. A settled task
 * never shows "upcoming" — nothing is left to reach for once there is no more run. Mirrors iOS/Android
 * 1:1 (`ChecklistState` / `StepState`). */
type ChecklistState = 'done' | 'current' | 'upcoming';

type Side = 'worker' | 'reviewer';

export function AgentAtWorkCard({ card }: { card: AgentAtWorkCardMarker }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const queryClient = useQueryClient();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const rosterName = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.name || undefined : undefined);

  // One scroll-stick ref per card — the worker and reviewer cards scroll independently.
  const workerTranscriptRef = useRef<HTMLElement>(null);

  const queryKey = ['task-at-work', card.taskId];
  const { data, isError, isLoading } = useQuery({
    queryKey,
    enabled: !!card.taskId,
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(card.taskId)}/at-work`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as AtWorkResponse;
    },
    // Stops the moment the task is terminal — no more rows will come.
    refetchInterval: (d) => (isTerminal((d as AtWorkResponse | undefined)?.status) ? false : POLL_MS),
  });

  // The card's own async region reports the 198:4 / 198:5 pair with real timing and outcome.
  useLoadBi('agent-at-work-card', { isLoading, isError });

  const workerConversationId = data?.workerConversationId ?? '';
  const reviewerConversationId = data?.reviewerConversationId ?? '';
  // Keyed on the reviewer's IDENTITY, not its conversation: the reviewer is chosen at SPAWN, so it is
  // known from the first frame — long before it has a conversation or anything to say. Keyed on the
  // conversation, the flip would be hidden for exactly the period when "who will check this?" is live.
  const hasReviewer = !!data?.reviewerAgentId;

  // Stream new rows live, for BOTH sides, into the same cache the poll fills. One subscription serves
  // both cards: the row's own conversationId decides which one it belongs to.
  useDuplexer(
    useCallback(
      (entity: { conversationId?: string }) => {
        const target: Side | null =
          entity.conversationId && entity.conversationId === workerConversationId
            ? 'worker'
            : entity.conversationId && entity.conversationId === reviewerConversationId
            ? 'reviewer'
            : null;
        if (!target) return;
        const msg = entityToMessage(entity as Parameters<typeof entityToMessage>[0]);
        if (!msg) return;
        queryClient.setQueryData<AtWorkResponse>(queryKey, (prev) => {
          if (!prev) return prev;
          const key = target === 'worker' ? 'workerRows' : 'reviewerRows';
          return { ...prev, [key]: upsertAgentAtWorkRow(prev[key] ?? [], msg) };
        });
      },
      [workerConversationId, reviewerConversationId, queryClient, card.taskId],
    ),
  );

  const stop = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(card.taskId)}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    // Re-read straight away rather than waiting out the poll: the user just acted, and a card that keeps
    // saying "In progress" afterwards reads as a button that did nothing. The status is deliberately NOT
    // written optimistically — this card's job is to show what is actually happening, and flipping it to
    // "Cancelled" before the agent has been halted would state that the work stopped when we only asked.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const terminal = isTerminal(data?.status);
  const live = !!data?.isLive && !terminal;

  const workerAgentId = card.agentId;
  const workerName = rosterName(workerAgentId) ?? card.agentName ?? t('chat.agentAtWork.worker');
  const workerColorKey = workerAgentId ?? workerName ?? '';

  const reviewerAgentId = data?.reviewerAgentId || undefined;
  // The reviewer has no first-frame hint on the row (it may not have existed at spawn), so it falls
  // back to the localized role word rather than a name.
  const reviewerName = rosterName(reviewerAgentId) ?? t('chat.agentAtWork.reviewer');
  const reviewerColorKey = reviewerAgentId ?? reviewerName ?? '';

  const verdict = data?.reviewVerdict ?? undefined;
  const taskTitle = data?.title || card.taskTitle || '';
  // ONE badge for one card, and the verdict outranks the status when there is one. A task whose work has
  // been handed to the reviewer still carries IN_PROGRESS on the row — the backend moves it on approval,
  // not on submission — so the status alone said "In progress" on a card whose own review section was
  // reading "In review" two lines below it.
  const statusBadge = taskStatusBadge(data?.status ?? 'IN_PROGRESS', { detail: true });
  const badge = verdict ? VERDICT_BADGE[verdict.stage] : statusBadge;

  // The task's own plan — the real sub-task list, belonging to the WORKER card only: the plan is the
  // task's as a whole, not either side's own conversation.
  const planSteps = data?.plan ?? [];
  const hasPlan = planSteps.length > 0;

  // A real plan already shows the worker's step narrations above, as the checklist — keeping 'narrate'
  // rows here too would say the same step twice. No plan yet means the transcript IS the checklist
  // (see `renderTranscript`), so nothing is dropped.
  const workerRows = buildRows(data?.workerRows ?? []);
  const visibleWorkerRows = agentAtWorkWindow(
    hasPlan ? collapseRepeatedActs(workerRows.filter((r) => r.kind !== 'narrate')) : workerRows,
  );

  // Stick each capped pane to its newest line while the work is live, and only then: once a run is over
  // the reader owns the scroll, and yanking them back to the bottom of a finished transcript they were
  // reading up through is the behaviour every chat gets wrong. Keyed on the rendered text as well as the
  // row count so a row GROWING (the typewriter, a streamed turn) keeps the newest words in view rather
  // than only a whole new row doing so.
  const newestWorkerText = visibleWorkerRows[visibleWorkerRows.length - 1]?.text ?? '';
  useEffect(() => {
    const pane = workerTranscriptRef.current;
    if (!pane || !live) return;
    pane.scrollTop = pane.scrollHeight;
  }, [live, visibleWorkerRows.length, newestWorkerText]);

  const showStop = !terminal && data?.found !== false;

  // Shared between the two cards: the capped, scrolling transcript pane, or the error/empty line in its
  // place. `emptyKey` is only passed for the worker card — the reviewer card's own block above it already
  // says where the review stands, so echoing "nothing here yet" under that says the absence twice.
  const renderTranscript = (
    rows: ReturnType<typeof buildRows>,
    ref: typeof workerTranscriptRef,
    sideAgentId: string | undefined,
    testId: string,
    isWorkerSide: boolean,
    emptyKey?: string,
  ) => {
    if (isError || data?.found === false) {
      // An explicit failure, never an empty transcript standing in for one.
      return (
        <Typography variant="p3" color="secondary" data-testid="agent-at-work-error">
          {t('tasks.detail.conversationError')}
        </Typography>
      );
    }
    if (rows.length === 0) {
      return emptyKey ? (
        <Typography variant="p3" color="secondary" data-testid="agent-at-work-empty">
          {t(emptyKey)}
        </Typography>
      ) : null;
    }
    // Which narrate row is the CURRENT step, for the REVIEWER side's own narrate rows (it has no plan of
    // its own to read this off). Mirrors iOS/Android `lastNarrateIndex`.
    const lastNarrateIndex = rows.reduce((last, r, i) => (r.kind === 'narrate' ? i : last), -1);
    return (
      /* A CAPPED, scrolling pane — the row window alone does not bound the card. `AGENT_AT_WORK_WINDOW`
         limits how many TURNS are kept, and a turn is not a line: one long reasoning turn filled a whole
         phone screen and pushed the composer and the rest of the conversation out of view. So the height
         is capped and the work scrolls inside it, stuck to the newest line while a run is live so it
         reads as a live pane rather than one the reader has to chase. */
      <ScrollArea ref={ref} className={styles.transcript} data-testid={testId}>
        {rows.map((row, i) => {
          // The WORKER card's whole body is one checklist, so every row here goes through
          // `ChecklistRow` — never a stray sentence with no prefix or a size that doesn't match its
          // neighbours:
          //  • a real plan exists → its checklist above already carries the done/current/upcoming
          //    states, so every transcript row here is just a supporting log line (dot, secondary —
          //    never "current", since the plan's own row already wears the spinner), settling to a
          //    check once the task is terminal (a finished task never shows a stray dot);
          //  • no plan yet → the transcript itself IS the checklist: every row is DONE except the very
          //    last, which is CURRENT while the task is live.
          if (isWorkerSide) {
            // With a plan on screen these rows are the LOG, not the list. They used to wear the same dot
            // the plan's own upcoming steps wear, so "Save the report to your space" and "Reading the
            // notes" read as one undifferentiated list and the reader could not tell the job from the
            // keystrokes. The plan keeps the checklist; the log gets its own quieter register.
            if (hasPlan) {
              return (
                <div key={`log-${i}-${row.text.slice(0, 24)}`} className={styles.logRow}>
                  <Typography variant="p3" component="span" color="secondary">
                    <CardTurnText text={row.text} animate={live && i === rows.length - 1} />
                  </Typography>
                </div>
              );
            }
            // No plan yet, so the transcript IS the checklist: every row done except the newest, which
            // carries the spinner while the run is live.
            return (
              <ChecklistRow
                key={`row-${i}-${row.text.slice(0, 24)}`}
                text={row.text}
                state={i === rows.length - 1 && live ? 'current' : 'done'}
              />
            );
          }

          // The REVIEWER side has no plan of its own, so it keeps the narrate-row check/spinner
          // treatment for its own step captions, act rows settling to done once terminal (same rule as
          // the worker's own log lines), and the shared turn rendering for spoken turns.
          const showsAgent = showsCardAttribution(rows, i, sideAgentId);
          const name = rosterName(row.agentId) ?? (row.agentId === card.agentId ? card.agentName : undefined);
          if (row.kind === 'narrate') {
            const isCurrent = i === lastNarrateIndex && live;
            return <ChecklistRow key={`narrate-${i}-${row.text}`} text={row.text} state={isCurrent ? 'current' : 'done'} />;
          }
          if (row.kind === 'act') {
            return <ChecklistRow key={`act-${i}-${row.text}`} text={row.text} state={terminal ? 'done' : 'upcoming'} />;
          }
          return (
            <div key={`turn-${i}-${row.text.slice(0, 24)}`} className={styles.turn}>
              {showsAgent && (
                <span className={styles.attr}>
                  {!!row.agentId && <AgentDot id={row.agentId} size={16} />}
                  {!!name && <Typography variant="h4">{name}</Typography>}
                </span>
              )}
              {/* Quiet by design — the whole transcript is small and secondary-ink, the register a
                  reasoning trace reads in, not the register of the agent's ANSWER. What the agent
                  finally says arrives in the chat as its own message; this is a window on it thinking,
                  so it must never compete with the surrounding conversation. */}
              <Typography variant="p3" component="span" color="secondary">
                <CardTurnText text={row.text} animate={live && i === rows.length - 1} />
              </Typography>
            </div>
          );
        })}
      </ScrollArea>
    );
  };

  return (
    // ONE card for one task. The reviewer used to get a card of its own directly below this one, which
    // meant the same task title twice, two badges that disagreed, and two stop-less boxes for a single
    // piece of work. The review is a section inside the card now, below the work it judges.
    <>
      <Card variant="sunken" className={styles.card} data-testid="agent-at-work-card">
        {/* Badge leads, on its own row — the card states its status before it names anyone. */}
        <div className={styles.headTop}>
          <Badge variant={badge.variant} label={t(badge.labelKey)} />
        </div>

        <div className={styles.head}>
          {/* The TASK leads this row: it is the thing the reader came to check on. The agent doing it
              trails alongside, named rather than centred — see the avatar block below. */}
          <Typography variant="h3" className={styles.taskTitle}>
            {taskTitle}
          </Typography>

          {/* The working agent's own small avatar dot plus its name — a name-tag, not the card's centre. */}
          <div className={styles.identity} data-testid="agent-at-work-identity">
            <AgentDot id={workerColorKey} size={16} />
            <span className={styles.nameRow}>
              <Typography variant="p2">{workerName}</Typography>
            </span>
          </div>
        </div>

        <div className={styles.divider} aria-hidden />

        {/* The real sub-task list — every step the card shows goes through the same `ChecklistRow`, so
            there is never a stray sentence with no prefix or a size that doesn't match its neighbours
            (the old separate "action sentence" line — no prefix, its own larger size, almost always a
            near-duplicate of the checklist's own current row — is gone). */}
        {hasPlan &&
          (() => {
            const currentIndex = planSteps.findIndex((s) => !s.completed);
            return (
              <div className={styles.planList} data-testid="agent-at-work-plan">
                {planSteps.map((step, i) => (
                  <ChecklistRow
                    key={`plan-${i}-${step.title}`}
                    text={step.title}
                    state={planStepState(i, currentIndex < 0 ? null : currentIndex, terminal)}
                  />
                ))}
              </div>
            );
          })()}

        {renderTranscript(
          visibleWorkerRows,
          workerTranscriptRef,
          workerAgentId,
          'agent-at-work-transcript',
          true,
          hasPlan ? undefined : 'chat.agentAtWork.noTurnsYet',
        )}

        {/* The review, inside the same card and below the work it judges. It appears the moment a review
            pass exists (`hasReviewer`, set when the reviewer is chosen at spawn, long before it has said
            anything).

            ONE LINE, and deliberately not a second copy of the work above it. The reviewer used to get
            its own plan-less checklist plus its own streamed transcript, which meant two things at once:
            it read NOTHING like the executor's block a few pixels above (narrate rows with ticks, act
            rows with dots, no plan), and when it settled it pasted its entire audit into the card. What
            the reader needs from a review is which of three states it is in, so that is all it says:
            waiting, reviewing (with a loader), or the verdict. A rejection also says WHY, because that
            one is actionable; an approval is just the word. */}
        {hasReviewer && (
          <>
            <div className={styles.divider} aria-hidden />

            <div className={styles.reviewHead} data-testid="agent-at-work-reviewer-identity">
              <AgentDot id={reviewerColorKey} size={16} />
              <Typography variant="p2">{reviewerName}</Typography>
              <span className={styles.reviewState} data-testid="agent-at-work-review">
                {verdict ? (
                  verdict.stage === 'submitted' ? (
                    <>
                      {live && <Spinner size={14} />}
                      <Typography variant="p3" color="secondary">
                        {t('chat.agentAtWork.awaitingReview', { reviewer: reviewerName })}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="p3" color="secondary">
                      {t(VERDICT_BADGE[verdict.stage].labelKey)}
                    </Typography>
                  )
                ) : (
                  <Typography variant="p3" color="secondary">
                    {t('chat.agentAtWork.preReview', { reviewer: reviewerName, agent: workerName })}
                  </Typography>
                )}
              </span>
            </div>

            {/* A verdict the reader has to ACT on carries its reasons; an approval does not. Still clamped,
                because a reviewer writing a full audit is doing its job and the card is not where that
                belongs — the task screen the card opens is. */}
            {verdict && verdict.stage !== 'submitted' && verdict.stage !== 'approved' && !!verdict.content && (
              <div className={styles.verdictText}>
                <LinkifiedText text={verdict.content} />
              </div>
            )}
          </>
        )}

        {showStop && (
          <div className={styles.foot}>
            {
              // LABELLED, not icon-only. This is the one control on the card and it is destructive-ish:
              // a bare square is a glyph a first-time reader has to guess at, and the guess it invites
              // ("record?") is the opposite of what it does. The word costs one short line at the card's
              // foot and the copy already existed for the aria-label.
              <Button
                variant="outlined"
                size="tiny"
                loading={stop.isLoading}
                startIcon={<Square size={12} aria-hidden />}
                onClick={() => {
                  logPageClickBi({ page_name: 'chat', button_name: 'agent_at_work_stop' });
                  stop.mutate();
                }}
                data-testid="agent-at-work-stop"
              >
                {t(stop.isLoading ? 'chat.agentAtWork.stopping' : 'chat.agentAtWork.stop')}
              </Button>
            }
          </div>
        )}
      </Card>
    </>
  );
}
